import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../auth/AuthContext";
import CTAButton from "./CTAButton";

const NAV_LINKS = [
  { to: "/pricing", label: "Pricing" },
  { to: "/security", label: "Security" },
  { to: "/docs", label: "Docs" },
];

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-rf-accent to-rf-accent-blue text-sm font-bold text-white">
        A
      </div>
      <span className="text-lg font-bold tracking-tight text-rf-text-primary">Anthurium</span>
    </Link>
  );
}

/**
 * The single most important interactive detail on the site (design-system/
 * landing.md §5): sign-up and sign-in are different destinations. Both now
 * route through their own dedicated intermediate screens (item 20) — /signup
 * and /login — instead of one of them jumping straight to an external GitHub
 * URL. Sending a returning user to the install flow still strands them on
 * GitHub's configure screen (the dead end patched in a78d33d) — that risk is
 * exactly why these stay two separate destinations, just both mediated by
 * our own screens now rather than one being external and one not. Auth state
 * comes from AuthContext, same source App.js uses.
 */
function AuthCta({ onNavigate, stacked }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="h-12 w-32" aria-hidden="true" />;
  }

  if (isAuthenticated) {
    return (
      <CTAButton to="/dashboard" onClick={onNavigate} className={stacked ? "w-full" : ""}>
        Dashboard →
      </CTAButton>
    );
  }

  return (
    <>
      <Link
        to="/login"
        onClick={onNavigate}
        className={`cursor-pointer text-sm font-medium text-rf-text-secondary transition-colors duration-200 hover:text-rf-text-primary ${
          stacked ? "text-center" : ""
        }`}
      >
        Sign in
      </Link>
      <CTAButton to="/signup" onClick={onNavigate} className={stacked ? "w-full" : ""}>
        Start free trial
      </CTAButton>
    </>
  );
}

const linkClasses = ({ isActive }) =>
  `cursor-pointer text-sm font-medium transition-colors duration-200 ${
    isActive ? "text-rf-text-primary" : "text-rf-text-secondary hover:text-rf-text-primary"
  }`;

export default function MarketingNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  return (
    <header
      className={`sticky top-0 z-50 h-16 border-b transition-colors duration-200 ${
        scrolled
          ? "border-rf-border bg-rf-bg-primary/80 backdrop-blur-md"
          : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-full max-w-6xl items-center justify-between px-6 md:px-8">
        <Logo />

        <nav className="hidden items-center gap-8 md:flex" aria-label="Main">
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} className={linkClasses}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-6 md:flex">
          <AuthCta />
        </div>

        <button
          type="button"
          className="flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-rf-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rf-accent md:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-x-0 bottom-0 top-16 z-40 flex flex-col gap-10 overflow-y-auto bg-rf-bg-primary px-6 py-10 md:hidden">
          <nav className="flex flex-col gap-6" aria-label="Mobile">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={closeMobile}
                className={({ isActive }) =>
                  `text-lg font-medium ${isActive ? "text-rf-text-primary" : "text-rf-text-secondary"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex flex-col items-stretch gap-4">
            <AuthCta onNavigate={closeMobile} stacked />
          </div>
        </div>
      )}
    </header>
  );
}
